import LoadingButton from "@mui/lab/LoadingButton"
import { Dialog, IconButton, Alert, Divider, Tab, Tabs, ToggleButton, ToggleButtonGroup } from "@mui/material"
import { Formik, useFormikContext } from "formik"
import { Check, X, Minimize2, Maximize2 } from "lucide-mui"
import { DateTime } from "luxon"
import React, { useState } from "react"

import type { VenuePlanData } from "@/components/plan"
import { RESERVATION_COLORS, type ReservationStatus } from "@/constants/reservations"
import i18n from "@/i18n"
import { formatTableName } from "@/shared/tables"

import ReservationPlanSelector from "../shared/reservation_plan_selector"

import CustomerSection from "./customer_section"
import NotesSection from "./notes_section"
import TimeSection from "./time_section"
import { useBookingState } from "./use_booking_state"
import type { CreateReservationFormValues, LocalNote, ReservationType } from "./use_booking_state"

const STYLES = {
  dialog: {
    paper: "max-w-5xl w-[calc(100%-64px)] h-[calc(100%-64px)]",
  },
  header: {
    base: "pl-4 pr-2 py-1 text-white text-sm flex flex-row space-between items-center",
    title: "flex-1",
  },
  content: {
    main: "flex flex-col md:flex-row flex-1 min-h-0",
  },
  leftColumn: {
    container: "w-full md:w-1/3 p-4 flex flex-col",
    customerSection: "space-y-3 mb-4",
    divider: "border-gray-300 my-4",
    reservationSection: "space-y-3",
    footer: "border-t border-gray-200 pt-4 mt-auto",
  },
  rightColumn: {
    container: "w-full md:w-2/3 flex flex-col min-h-0 border-l border-gray-200",
    tabsContainer: "border-b border-gray-200 flex-shrink-0",
    tabContent: "flex-1 min-h-0 overflow-hidden",
    mapContainer: "h-full p-4",
  },
}

type FormBodyProps = {
  venue: any
  venuePlans: VenuePlanData[]
  reservationBuffer: number
  fullScreen: boolean
  setFullScreen: (v: boolean) => void
  localNotes: LocalNote[]
  handleAddNote: (note: string) => void
  setMapHasPendingChanges: (v: boolean) => void
  error: string | null
  success: boolean | null
  creating: boolean
  reservationType: ReservationType
  handleReservationTypeChange: (type: ReservationType, setFieldValue: (f: string, v: any) => any) => void
  handleClose: () => void
}

const FormBody: React.FC<FormBodyProps> = ({
  venue,
  venuePlans,
  reservationBuffer,
  fullScreen,
  setFullScreen,
  localNotes,
  handleAddNote,
  setMapHasPendingChanges,
  error,
  success,
  creating,
  reservationType,
  handleReservationTypeChange,
  handleClose,
}) => {
  const formik = useFormikContext<CreateReservationFormValues>()
  const [selectedTab, setSelectedTab] = useState(0)

  const selectedTableIds = formik.values.tableSlots.map((slot) => slot.tableId)
  const firstSlot = formik.values.tableSlots[0]

  const handleAddTableSlot = (tableId: string, startTime: DateTime, endTime: DateTime) => {
    const activeTables = venue.tables.filter((t: any) => !t.deleted)
    const table = activeTables.find((t: any) => t.id === tableId)
    const tableName = table ? formatTableName(table) || tableId : tableId
    const existingIndex = formik.values.tableSlots.findIndex((slot) => slot.tableId === tableId)

    if (existingIndex >= 0) {
      const updatedSlots = [...formik.values.tableSlots]
      updatedSlots[existingIndex] = {
        tableId,
        tableName,
        startTime: startTime.toISO() || "",
        endTime: endTime.toISO() || "",
      }
      void formik.setFieldValue("tableSlots", updatedSlots)
    } else {
      void formik.setFieldValue("tableSlots", [
        ...formik.values.tableSlots,
        { tableId, tableName, startTime: startTime.toISO() || "", endTime: endTime.toISO() || "" },
      ])
    }
  }

  return (
    <form onSubmit={formik.handleSubmit} className="flex flex-col h-full">
      <div
        className={STYLES.header.base}
        style={{
          backgroundColor:
            RESERVATION_COLORS[formik.values.status as ReservationStatus]?.main || RESERVATION_COLORS["confirmed"].main,
        }}
      >
        <div className={STYLES.header.title}>
          {reservationType === "walk_in"
            ? i18n.t("app.create_reservation.title_add_walk_in")
            : i18n.t("app.create_reservation.title")}
        </div>
        <IconButton
          onClick={() => setFullScreen(!fullScreen)}
          color="inherit"
          size="small"
          data-testid="panel-reservations-create-form-fullscreen-btn"
        >
          {fullScreen ? <Minimize2 className="text-base" /> : <Maximize2 className="text-base" />}
        </IconButton>
        <IconButton
          onClick={handleClose}
          color="inherit"
          size="small"
          data-testid="panel-reservations-create-form-close-btn"
        >
          <X className="text-lg" />
        </IconButton>
      </div>

      {!!error && (
        <Alert severity="error" className="m-2">
          {error}
        </Alert>
      )}

      <div className={STYLES.content.main}>
        <div className={STYLES.leftColumn.container}>
          <ToggleButtonGroup
            value={reservationType}
            exclusive
            onChange={(_, newType) => {
              if (newType !== null) handleReservationTypeChange(newType, formik.setFieldValue)
            }}
            className="w-full mb-4"
            size="small"
          >
            <ToggleButton
              value="reservation"
              className="flex-1 text-sm"
              data-testid="panel-reservations-create-form-type-reservation"
            >
              {i18n.t("app.create_reservation.type.reservation")}
            </ToggleButton>
            <ToggleButton
              value="walk_in"
              className="flex-1 text-sm"
              data-testid="panel-reservations-create-form-type-walkin"
            >
              {i18n.t("app.create_reservation.type.walk_in")}
            </ToggleButton>
          </ToggleButtonGroup>

          <div className={STYLES.leftColumn.customerSection}>
            <CustomerSection />
          </div>

          <Divider className={STYLES.leftColumn.divider} />

          <div className={STYLES.leftColumn.reservationSection}>
            <TimeSection venue={venue} reservationType={reservationType} />
          </div>

          <div className={STYLES.leftColumn.footer}>
            <LoadingButton
              color={success ? "success" : "primary"}
              loadingPosition="center"
              loading={creating}
              type="submit"
              onClick={formik.submitForm}
              fullWidth
              variant="contained"
              data-testid="panel-reservations-create-form-submit-btn"
            >
              {success ? <Check /> : i18n.t("app.actions.save")}
            </LoadingButton>
          </div>
        </div>

        <div className={STYLES.rightColumn.container}>
          <div className={STYLES.rightColumn.tabsContainer}>
            <Tabs value={selectedTab} onChange={(_, v) => setSelectedTab(v)}>
              <Tab label={i18n.t("app.create_reservation.tabs.venue_plan")} />
              <Tab label={`${i18n.t("app.create_reservation.tabs.comments")} (${localNotes.length})`} />
            </Tabs>
          </div>
          <div className={`${STYLES.rightColumn.tabContent} overflow-y-auto`}>
            {selectedTab === 0 && (
              <div className={STYLES.rightColumn.mapContainer}>
                <ReservationPlanSelector
                  venue={venue}
                  venuePlans={venuePlans}
                  selectedTableIds={selectedTableIds}
                  date={formik.values.date}
                  startTime={firstSlot?.startTime || null}
                  endTime={firstSlot?.endTime || null}
                  onAddTableSlot={handleAddTableSlot}
                  onPendingChangesChange={setMapHasPendingChanges}
                  fullScreen={fullScreen}
                  multiSelect
                  reservationBuffer={reservationBuffer}
                  mode="add"
                  existingTableSlots={formik.values.tableSlots}
                />
              </div>
            )}
            {selectedTab === 1 && <NotesSection localNotes={localNotes} onAddNote={handleAddNote} />}
          </div>
        </div>
      </div>
    </form>
  )
}

const CreateForm: React.FC = () => {
  const state = useBookingState()

  return (
    <Dialog
      open={state.showCreateReservationDialog}
      onClose={state.handleClose}
      PaperProps={{ className: STYLES.dialog.paper }}
      scroll="body"
      fullScreen={state.fullScreen}
      data-testid="panel-reservations-create-form"
    >
      <Formik
        initialValues={state.initialValues}
        enableReinitialize
        validationSchema={state.validationSchema}
        onSubmit={state.onSubmit}
      >
        <FormBody
          venue={state.venue}
          venuePlans={state.venuePlans}
          reservationBuffer={state.reservationBuffer}
          fullScreen={state.fullScreen}
          setFullScreen={state.setFullScreen}
          localNotes={state.localNotes}
          handleAddNote={state.handleAddNote}
          setMapHasPendingChanges={state.setMapHasPendingChanges}
          error={state.error}
          success={state.success}
          creating={state.creating}
          reservationType={state.reservationType}
          handleReservationTypeChange={state.handleReservationTypeChange}
          handleClose={state.handleClose}
        />
      </Formik>
    </Dialog>
  )
}

export default CreateForm
